/**
 * Declarative analytics bindings.
 *
 * Markup describes interactions with `data-mnl-*` attributes and this module
 * forwards them to `window.MNLAnalytics`:
 * - `data-mnl-action` on an interactive element or its wrapper defines the event;
 * - other `data-mnl-*` attributes become snake_case event parameters;
 * - `data-mnl-promotion-id` marks a promotion container for promotion_view
 * and promotion_click when no more specific action applies.
 */

const ACTION_SELECTOR = '[data-mnl-action]';
const PROMOTION_SELECTOR = '[data-mnl-promotion-id]';
const INTERACTIVE_SELECTOR = 'a[href], button, input[type="submit"], input[type="button"], [role="button"], [role="link"]';
const PROMOTION_VISIBLE_RATIO = 0.5;
const PROMOTION_VISIBLE_DELAY = 1000;
const DRAG_THRESHOLD = 8;

let pointerStart = null;

/**
 * Whether a promotion is eligible for auto-observed promotion_view tracking.
 *
 * Mega-menu promos are tracked when the submenu is explicitly opened, so the
 * generic observer must ignore closed/unmounted hosts.
 *
 * @param {HTMLElement} promotion Promotion container.
 * @return {boolean} Whether observer-based tracking should run.
 */
const isPromotionTrackable = ( promotion ) => {

	if ( ! promotion ) {

		return false;

	}

	if ( promotion.dataset.mnlPromotionLocation !== 'mega_menu' ) {

		return true;

	}

	const submenu = promotion.closest( '.b-item-submenu' );

	return promotion.dataset.templateMounted === 'true'
		&& !! submenu
		&& submenu.classList.contains( 'b-item-submenu--active' );

};

/**
 * Converts a `data-mnl-*` dataset key to an analytics parameter name.
 *
 * @param {string} key Dataset key, e.g. `mnlGameId`.
 * @return {string} Parameter name, e.g. `game_id`.
 */
const toParameterName = ( key ) => key
	.slice( 3 )
	.replace( /[A-Z]/g, ( letter ) => `_${ letter.toLowerCase() }` )
	.replace( /^_/, '' );

/**
 * Reads analytics parameters from `data-mnl-*` attributes of an element.
 *
 * @param {HTMLElement} element Source element.
 * @return {Object<string, string>} Event parameters.
 */
export const getParameters = ( element ) => {

	const parameters = {};

	Object.keys( element.dataset ).forEach( ( key ) => {

		if ( key.startsWith( 'mnl' ) && key !== 'mnlAction' ) {

			parameters[ toParameterName( key ) ] = element.dataset[ key ];

		}

	} );

	return parameters;

};

/**
 * Sends content_engagement with the content context exposed on a block root.
 *
 * @param {HTMLElement} block             Block root with `data-mnl-*` attributes.
 * @param {string}      action            Stable engagement action.
 * @param {string}      interactionMethod Stable name of the control or path that triggered the action.
 * @return {void}
 */
export const trackContentEngagement = ( block, action, interactionMethod ) => {

	window.MNLAnalytics?.track( 'content_engagement', {
		...getParameters( block ),
		action,
		interaction_method: interactionMethod,
	} );

};

/**
 * Builds a query-free destination for a link so URL parameters never reach analytics.
 *
 * @param {Element} element Interactive element.
 * @return {string|undefined} Destination path or external URL.
 */
const getDestination = ( element ) => {

	if ( ! ( element instanceof HTMLAnchorElement ) || ! element.href ) {

		return undefined;

	}

	const url = new URL( element.href, window.location.href );

	if ( url.protocol !== 'http:' && url.protocol !== 'https:' ) {

		return undefined;

	}

	if ( url.origin === window.location.origin ) {

		return url.pathname + url.hash;

	}

	return url.origin + url.pathname;

};

/**
 * Collects parameters from a wrapper and the clicked element, the latter taking precedence.
 *
 * @param {HTMLElement} contextElement Element defining the event context.
 * @param {HTMLElement} interactive Clicked interactive element.
 * @return {Object<string, string>} Event parameters.
 */
const getClickParameters = ( contextElement, interactive ) => ( {
	destination: getDestination( interactive ),
	...getParameters( contextElement ),
	...( contextElement !== interactive ? getParameters( interactive ) : {} ),
} );

/**
 * Remembers where a pointer press started, to tell clicks from drags.
 *
 * @param {PointerEvent} event Pointerdown event.
 * @return {void}
 */
const handlePointerDown = ( event ) => {

	pointerStart = { x: event.clientX, y: event.clientY };

};

/**
 * Determines whether this click is the end of a drag gesture (for example, swiping a slider) instead of an intentional activation.
 *
 * @param {MouseEvent} event Click event.
 * @return {boolean} Whether the pointer moved beyond the drag threshold.
 */
const isDragClick = ( event ) => {

	// Keyboard activation has no pointer press.
	if ( ! pointerStart || event.detail === 0 ) {

		return false;

	}

	const distance = Math.hypot( event.clientX - pointerStart.x, event.clientY - pointerStart.y );

	pointerStart = null;

	return distance > DRAG_THRESHOLD;

};

/**
 * Tracks clicks on elements described by `data-mnl-action` or inside promotions.
 *
 * @param {MouseEvent} event Click event.
 * @return {void}
 */
const handleClick = ( event ) => {

	if ( ! ( event.target instanceof Element ) || isDragClick( event ) ) {

		return;

	}

	const interactive = event.target.closest( INTERACTIVE_SELECTOR );

	// Form submissions are tracked by the submit handler; Enter also dispatches a click on the submit button.
	if ( ! interactive || ( interactive.form && interactive.type === 'submit' ) ) {

		return;

	}

	// Wrappers describe their links only; other controls (sliders, toggles) need their own data-mnl-action.
	const isLink = interactive.matches( 'a[href]' );
	const actionElement = isLink || interactive.matches( ACTION_SELECTOR ) ? interactive.closest( ACTION_SELECTOR ) : null;
	const promotionElement = isLink ? interactive.closest( PROMOTION_SELECTOR ) : null;

	// The nearest context wins so each interaction produces exactly one event.
	if ( actionElement && ( ! promotionElement || promotionElement.contains( actionElement ) ) ) {

		const eventName = actionElement.dataset.mnlAction;
		const parameters = getClickParameters( actionElement, interactive );

		if ( ( eventName === 'navigation_click' || eventName === 'cta_click' ) && ! parameters.link_text ) {

			parameters.link_text = interactive.textContent.replace( /\s+/g, ' ' ).trim();

		}

		window.MNLAnalytics.track( eventName, parameters );

		return;

	}

	if ( promotionElement ) {

		window.MNLAnalytics.track( 'promotion_click', getClickParameters( promotionElement, interactive ) );

	}

};

/**
 * Tracks form submissions described by `data-mnl-action` without reading field values.
 *
 * @param {SubmitEvent} event Submit event.
 * @return {void}
 */
const handleSubmit = ( event ) => {

	if ( ! ( event.target instanceof HTMLFormElement ) ) {

		return;

	}

	const actionElement = event.target.closest( ACTION_SELECTOR );

	if ( ! actionElement ) {

		return;

	}

	const parameters = getParameters( actionElement );

	if ( event.submitter instanceof HTMLElement ) {

		Object.assign( parameters, getParameters( event.submitter ) );

	}

	window.MNLAnalytics.track( actionElement.dataset.mnlAction, parameters );

};

/**
 * Checks whether a promotion occupies enough of the element or of the viewport.
 *
 * Tall promotions can never reach the ratio of their own area, so viewport coverage also counts.
 *
 * @param {IntersectionObserverEntry} entry Observer entry.
 * @return {boolean} Whether the promotion is meaningfully visible.
 */
const isPromotionVisible = ( entry ) => {

	if ( ! entry.isIntersecting ) {

		return false;

	}

	const viewportHeight = entry.rootBounds ? entry.rootBounds.height : window.innerHeight;

	return entry.intersectionRatio >= PROMOTION_VISIBLE_RATIO
		|| entry.intersectionRect.height >= viewportHeight * PROMOTION_VISIBLE_RATIO;

};

/**
 * Tracks promotion_view once per promotion per page view after it stays visible long enough.
 *
 * @return {void}
 */
const initPromotionViews = () => {

	const promotions = document.querySelectorAll( PROMOTION_SELECTOR );

	if ( ! promotions.length || ! ( 'IntersectionObserver' in window ) ) {

		return;

	}

	const timers = new Map();

	const observer = new IntersectionObserver( ( entries ) => {

		entries.forEach( ( entry ) => {

			const promotion = entry.target;

			if ( ! isPromotionTrackable( promotion ) ) {

				window.clearTimeout( timers.get( promotion ) );
				timers.delete( promotion );

				return;

			}

			if ( ! isPromotionVisible( entry ) ) {

				window.clearTimeout( timers.get( promotion ) );
				timers.delete( promotion );

				return;

			}

			if ( timers.has( promotion ) ) {

				return;

			}

			timers.set( promotion, window.setTimeout( () => {

				const parameters = getParameters( promotion );
				const key = `promotion_view:${ parameters.promotion_id }:${ parameters.promotion_location || '' }`;

				window.MNLAnalytics.trackOnce( key, 'promotion_view', parameters );
				timers.delete( promotion );
				observer.unobserve( promotion );

			}, PROMOTION_VISIBLE_DELAY ) );

		} );

	}, { threshold: [ 0, 0.25, 0.5, 0.75, 1 ] } );

	promotions.forEach( ( promotion ) => observer.observe( promotion ) );

};

/**
 * Tracks middle-button clicks, which open links in a new tab without dispatching `click`.
 *
 * @param {MouseEvent} event Auxclick event.
 * @return {void}
 */
const handleAuxClick = ( event ) => {

	// Middle click activates only links; on buttons it has no effect.
	if ( event.button !== 1 || ! ( event.target instanceof Element ) || ! event.target.closest( 'a[href]' ) ) {

		return;

	}

	handleClick( event );

};

/**
 * Initializes declarative analytics tracking.
 *
 * @return {void}
 */
export default function initAnalytics() {

	if ( ! window.MNLAnalytics ) {

		return;

	}

	document.addEventListener( 'pointerdown', handlePointerDown, true );
	document.addEventListener( 'click', handleClick, true );
	document.addEventListener( 'auxclick', handleAuxClick, true );
	document.addEventListener( 'submit', handleSubmit, true );
	initPromotionViews();

}
